import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../constants/config";
import { RefreshCw, Filter, X, TrendingUp, AlertTriangle, AlertCircle, Activity, BarChart3, Building2 } from "lucide-react";
import TrendsChart from "./TrendsChart";

// Utility function to normalize IP address to IPv4
const normalizeIpToIPv4 = (ip) => {
  if (!ip || ip === '::1' || ip === 'localhost') {
    return ip;
  }
  
  // Handle IPv6-mapped IPv4 addresses (::ffff:192.168.1.1)
  if (ip.startsWith('::ffff:')) {
    return ip.replace('::ffff:', '');
  }
  
  // If it's a pure IPv6 address (not mapped), return null
  if (ip.includes(':') && !ip.match(/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/)) {
    const ipv6MappedMatch = ip.match(/::ffff:(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})/);
    if (ipv6MappedMatch) {
      return ipv6MappedMatch[1];
    }
    return null;
  }
  
  // Already IPv4 format
  return ip;
};

function LogsViewer() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("schools"); // Default to schools list
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState(null);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    method: "",
    url: "",
    platform: "",
    ip: "",
    userType: "",
    userId: ""
  });
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    totalPages: 0,
    pageSize: 50
  });

  // Fetch schools on mount
  useEffect(() => {
    fetchSchools();
  }, []);

  // Handle tab changes
  useEffect(() => {
    if (activeTab === "stats") {
      fetchStats();
      fetchTrends();
    } else if (activeTab === "schools") {
      fetchSchools();
    } else if (activeTab === "superadmin") {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchSuperAdminLogs();
    }
  }, [activeTab]);

  // Handle filter changes for superadmin
  useEffect(() => {
    if (activeTab === "superadmin") {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchSuperAdminLogs();
    }
  }, [filters]);

  // Handle pagination changes for superadmin
  useEffect(() => {
    if (activeTab === "superadmin") {
      fetchSuperAdminLogs();
    }
  }, [pagination.page]);


  const fetchSuperAdminLogs = async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      params.append("page", pagination.page);
      
      // Super Admin tab: always fetch superadmin logs only
      params.append("userType", "superadmin");
      if (filters.platform) params.append("platform", filters.platform);
      if (filters.ip) params.append("ip", filters.ip);
      if (filters.status) params.append("status", filters.status);
      if (filters.method) params.append("method", filters.method);
      if (filters.url) params.append("url", filters.url);
      
      const response = await axiosInstance.get(API_ENDPOINTS.logsRequests(params.toString()));
      
      // Sort logs: success first (status < 400), then failures (400-499), errors last (status >= 500)
      const sortedLogs = (response.data.logs || []).sort((a, b) => {
        const aStatus = a.status || 0;
        const bStatus = b.status || 0;
        
        // Categorize: 0 = success (< 400), 1 = failure (400-499), 2 = error (>= 500)
        const getCategory = (status) => {
          if (status < 400) return 0; // Success
          if (status < 500) return 1; // Failure
          return 2; // Error
        };
        
        const aCategory = getCategory(aStatus);
        const bCategory = getCategory(bStatus);
        
        // Sort by category first (success < failure < error)
        if (aCategory !== bCategory) {
          return aCategory - bCategory;
        }
        
        // Within same category, sort by timestamp (newest first)
        return new Date(b.timestamp) - new Date(a.timestamp);
      });
      
      setLogs(sortedLogs);
      if (response.data.total !== undefined) {
        setPagination(prev => ({
          ...prev,
          page: response.data.page || 1,
          total: response.data.total || 0,
          totalPages: response.data.totalPages || 0
        }));
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch superadmin logs");
      setLogs([]);
      setPagination(prev => ({ ...prev, page: 1, total: 0, totalPages: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.logsStats);
      setStats(response.data.stats);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch statistics");
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrends = async () => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.logsTrends("days=14"));
      setTrends(response.data.trends);
    } catch {
      setTrends(null);
    }
  };

  const fetchSchools = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.schools);
      setSchools(response.data.data?.schools || []);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch schools");
      setSchools([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSchoolClick = (schoolId) => {
    navigate(`/dashboard/system-logs/school/${schoolId}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getStatusColor = (status) => {
    if (!status) return "bg-gray-500";
    if (status >= 500) return "bg-red-600";
    if (status >= 400) return "bg-orange-500";
    if (status >= 300) return "bg-blue-500";
    return "bg-green-500";
  };

  const tabs = [
    { id: "schools", label: "Schools", icon: <Building2 className="w-4 h-4" /> },
    { id: "superadmin", label: "Super Admin Logs", icon: <Activity className="w-4 h-4" /> },
    { id: "stats", label: "Statistics", icon: <BarChart3 className="w-4 h-4" /> }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">System Logs</h2>
        <button
          onClick={() => {
            if (activeTab === "stats") { fetchStats(); fetchTrends(); }
            else if (activeTab === "schools") fetchSchools();
            else if (activeTab === "superadmin") fetchSuperAdminLogs();
            else fetchLogs();
          }}
          disabled={loading}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-md">
        <div className="border-b border-gray-200">
          <div className="flex space-x-1 p-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === tab.id
                    ? "bg-blue-600 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {tab.icon}
                <span className="ml-2">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {activeTab === "superadmin" && (
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center mb-2">
              <Filter className="w-4 h-4 mr-2 text-gray-600" />
              <span className="font-medium text-gray-700">Filters</span>
              <span className="ml-2 text-xs text-gray-500">(Super Admin logs only)</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
                <select
                  value={filters.platform}
                  onChange={(e) => setFilters({ ...filters, platform: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All</option>
                  <option value="app">App</option>
                  <option value="web">Web</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">IP Address</label>
                <input
                  type="text"
                  value={filters.ip}
                  onChange={(e) => setFilters({ ...filters, ip: e.target.value })}
                  placeholder="Search by IP"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status Code</label>
                <input
                  type="number"
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  placeholder="e.g., 500"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Method</label>
                <select
                  value={filters.method}
                  onChange={(e) => setFilters({ ...filters, method: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All</option>
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="PATCH">PATCH</option>
                  <option value="DELETE">DELETE</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL Contains</label>
                <input
                  type="text"
                  value={filters.url}
                  onChange={(e) => setFilters({ ...filters, url: e.target.value })}
                  placeholder="e.g., /api/users"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            {(filters.status || filters.method || filters.url || filters.platform || filters.ip) && (
              <button
                onClick={() => {
                  setFilters({ ...filters, status: "", method: "", url: "", platform: "", ip: "" });
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="mt-3 text-sm text-blue-600 hover:text-blue-800 flex items-center"
              >
                <X className="w-4 h-4 mr-1" />
                Clear Filters
              </button>
            )}
          </div>
        )}

        {error && (
          <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700">
            {error}
          </div>
        )}

        <div className="p-4">
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-blue-600" />
              <p className="mt-2 text-gray-600">Loading logs...</p>
            </div>
          ) : activeTab === "stats" ? (
            stats ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                    <div className="flex items-center mb-2">
                      <Activity className="w-5 h-5 text-blue-600 mr-2" />
                      <span className="text-sm font-medium text-gray-600">Total Requests</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-600">{stats.totalRequests || 0}</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                    <div className="flex items-center mb-2">
                      <TrendingUp className="w-5 h-5 text-green-600 mr-2" />
                      <span className="text-sm font-medium text-gray-600">Successful</span>
                    </div>
                    <p className="text-2xl font-bold text-green-600">{stats.successfulRequests || 0}</p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-500">
                    <div className="flex items-center mb-2">
                      <AlertTriangle className="w-5 h-5 text-orange-600 mr-2" />
                      <span className="text-sm font-medium text-gray-600">Failed</span>
                    </div>
                    <p className="text-2xl font-bold text-orange-600">{stats.failedRequests || 0}</p>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-500">
                    <div className="flex items-center mb-2">
                      <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                      <span className="text-sm font-medium text-gray-600">Errors</span>
                    </div>
                    <p className="text-2xl font-bold text-red-600">{stats.errors || 0}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <h3 className="font-semibold mb-3">Last 24 Hours</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Requests:</span>
                        <span className="font-medium">{stats.last24Hours?.requests || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Failures:</span>
                        <span className="font-medium text-orange-600">{stats.last24Hours?.failures || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Errors:</span>
                        <span className="font-medium text-red-600">{stats.last24Hours?.errors || 0}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <h3 className="font-semibold mb-3">Top Endpoints</h3>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {stats.topEndpoints && Object.entries(stats.topEndpoints).length > 0 ? (
                        Object.entries(stats.topEndpoints)
                          .sort((a, b) => b[1] - a[1])
                          .slice(0, 10)
                          .map(([url, count]) => (
                            <div key={url} className="flex justify-between text-sm">
                              <span className="text-gray-600 truncate">{url}</span>
                              <span className="font-medium">{count}</span>
                            </div>
                          ))
                      ) : (
                        <p className="text-gray-500 text-sm">No data available</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <h3 className="font-semibold mb-3">Status Codes</h3>
                    <div className="space-y-2">
                      {stats.statusCodes && Object.entries(stats.statusCodes).length > 0 ? (
                        Object.entries(stats.statusCodes)
                          .sort((a, b) => b[1] - a[1])
                          .map(([status, count]) => (
                            <div key={status} className="flex items-center justify-between">
                              <div className="flex items-center">
                                <span className={`w-3 h-3 rounded-full mr-2 ${getStatusColor(parseInt(status))}`}></span>
                                <span className="text-gray-600">{status}</span>
                              </div>
                              <span className="font-medium">{count}</span>
                            </div>
                          ))
                      ) : (
                        <p className="text-gray-500 text-sm">No data available</p>
                      )}
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <h3 className="font-semibold mb-3">HTTP Methods</h3>
                    <div className="space-y-2">
                      {stats.methods && Object.entries(stats.methods).length > 0 ? (
                        Object.entries(stats.methods)
                          .sort((a, b) => b[1] - a[1])
                          .map(([method, count]) => (
                            <div key={method} className="flex justify-between">
                              <span className="text-gray-600">{method}</span>
                              <span className="font-medium">{count}</span>
                            </div>
                          ))
                      ) : (
                        <p className="text-gray-500 text-sm">No data available</p>
                      )}
                    </div>
                  </div>
                </div>

                <TrendsChart trends={trends} />
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">No statistics available</div>
            )
          ) : activeTab === "schools" ? (
            loading ? (
              <div className="text-center py-8">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-blue-600" />
                <p className="mt-2 text-gray-600">Loading schools...</p>
              </div>
            ) : schools.length > 0 ? (
              <div className="overflow-x-auto">
                <div className="mb-4 text-sm text-gray-600">
                  Total Schools: <span className="font-semibold">{schools.length}</span> | Click on a school to view its logs
                </div>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">School Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Domain</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Users</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {schools.map((school) => (
                      <tr key={school._id} className="hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => handleSchoolClick(school._id)}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {school.name || "N/A"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {school.domain || "-"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {school.email || "-"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800">
                            {school.userCount || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSchoolClick(school._id);
                            }}
                            className="text-blue-600 hover:text-blue-800 font-medium hover:underline"
                          >
                            View Logs →
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Building2 className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                <p>No schools found</p>
              </div>
            )
          ) : activeTab === "superadmin" && logs.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">URL</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Response Time</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {logs.map((log, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(log.timestamp)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {log.platform ? (
                          <span className={`px-2 py-1 text-xs font-medium rounded ${
                            log.platform === 'app' 
                              ? 'bg-purple-100 text-purple-800' 
                              : log.platform === 'web'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {log.platform.toUpperCase()}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {log.method ? (
                          <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800">
                            {log.method}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">
                        {log.url || "-"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 max-w-md" title={log.error?.message || log.error?.name || log.message}>
                        <div className="truncate">
                          {log.error?.message || log.error?.name || log.message || "-"}
                        </div>
                        {log.error?.stack && (
                          <details className="mt-1">
                            <summary className="text-xs text-blue-600 cursor-pointer hover:text-blue-800">
                              View Stack Trace
                            </summary>
                            <pre className="text-xs text-gray-600 mt-1 p-2 bg-gray-50 rounded overflow-auto max-h-40">
                              {log.error.stack}
                            </pre>
                          </details>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {log.status ? (
                          <span className={`px-2 py-1 text-xs font-medium rounded text-white ${getStatusColor(log.status)}`} title={`HTTP ${log.status}`}>
                            {log.status}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {log.responseTime || "-"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {log.userInfo ? (
                          <div className="flex flex-col">
                            <span className="text-gray-900 font-medium">
                              {log.userInfo.displayName || 'Unknown'}
                            </span>
                            {log.userInfo.type && log.userInfo.type !== 'anonymous' && (
                              <span className="text-xs text-gray-500 capitalize">
                                {log.userInfo.type}
                              </span>
                            )}
                          </div>
                        ) : log.userId && log.userId !== 'anonymous' ? (
                          <span className="text-gray-900 font-medium" title={log.userId}>
                            {log.userId.length > 30 ? `${log.userId.substring(0, 30)}...` : log.userId}
                          </span>
                        ) : (
                          <span className="text-gray-400 italic">Anonymous</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {(() => {
                          const normalizedIp = normalizeIpToIPv4(log.ip);
                          if (normalizedIp && normalizedIp !== '::1' && normalizedIp !== 'localhost') {
                            return <span className="text-gray-900 font-mono text-xs">{normalizedIp}</span>;
                          } else if (log.ip === '::1' || normalizedIp === '::1') {
                            return <span className="text-gray-500" title="Localhost">localhost</span>;
                          } else {
                            return <span className="text-gray-400">-</span>;
                          }
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : activeTab === "superadmin" ? (
            <div className="text-center py-8 text-gray-500">
              <Activity className="w-12 h-12 mx-auto text-gray-400 mb-2" />
              <p>No superadmin logs found</p>
            </div>
          ) : null}
          
          {/* Pagination */}
          {logs.length > 0 && pagination.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
              <div className="text-sm text-gray-700">
                Showing {((pagination.page - 1) * pagination.pageSize) + 1} to {Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total} results
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page >= pagination.totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LogsViewer;

